import ComponentManager, {ComponentOptions, ComponentOptionsValuePlaceholder} from "../../componentFramework";
import {ExpandableArea, ExpandableControl} from "../Expandable";
import EventListenerManager from "../EventListenerManager";
import EditContentNode from "../EditContentNode";
import EditContentNodeControls from "../EditContentNodeControls";


const defaultOptions = {
	nodeId: ComponentOptionsValuePlaceholder.number.required,
	depth: ComponentOptionsValuePlaceholder.number.required,
	mapKeyOptions: ComponentOptionsValuePlaceholder.array.optional
};
export type DefaultOptionsType = typeof defaultOptions;

/**
 * Takes care of expanding and collapsing of groups of EditContentNodes
 */
@ComponentManager.register({requires: () => EditContentNode})
export default class EditContentGroup {
	public static componentStyleClass = "editContentGroup";

	protected readonly options: ComponentOptions<DefaultOptionsType>;
	private static readonly focusedStyleClass = "editContentGroup--containsFocus";
	private static readonly focusedChildStyleClass = "editContentGroup--childHasFocus";
	private static readonly containsMessageStyleClass = "editContentGroup--containsMessage";
	private static readonly expandedBodyStyleClass = "editContentGroup--expanded";

	private readonly eventListenerManager = new EventListenerManager();

	constructor(public readonly rootElement: HTMLElement) {
		this.options = new ComponentOptions(this, rootElement, defaultOptions);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
	}

	@ComponentManager.on("constructed")
	protected componentConstructed() {
		const expandableControl = this.getExpandableControl();
		if (expandableControl !== undefined) {
			expandableControl.setSiblingsContainer(
				this.rootElement.parentElement!.closest(".editContentGroup__bodyInner, .editContentNodes")
			);
			expandableControl.setSelectorForSiblings(
				":scope > .editContentGroup > .editContentGroup__header .editContentGroup__expandableControl"
			);
		}

		this.determineIfContainsFocusOrMessagesOrExpandChange(true);
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		const rootElementManaged = this.eventListenerManager.manage(this.rootElement);

		// Setup listeners to call "determineIfContainsFocusOrMessagesOrExpandChange" at appropriate times
		// Do this here instead of in constructor because determineIfContainsFocusOrMessagesOrExpandChange is called
		// at "constructed" event once and then we start watching after the DOM is fully ready.
		rootElementManaged.addEventListener("editContentNodeFocusChange", this.onChange);
		rootElementManaged.addEventListener("editContentNodeFieldMessageChange", this.onChange);

		this.eventListenerManager.manage(this.rootElement).addEventListener("editContentNodeReady", () => {
			this.determineIfContainsFocusOrMessagesOrExpandChange(true);
		});

		// Listen for body being clicked. When it is update focus. Also stop clicking through.
		const body = this.getBody();
		if (body !== null) {
			this.eventListenerManager.manage(body).addEventListener("click", this.onBodyOrHeaderClick);
		}
		const header = this.getHeader();
		if (header !== null) {
			this.eventListenerManager.manage(header).addEventListener("click", this.onBodyOrHeaderClick);
		}

		// Listen for change in expandable area
		const expandableArea = this.getExpandableArea();
		if (expandableArea !== undefined) {
			this.eventListenerManager.manage(expandableArea)?.addEventListener("expandChange", this.onChange);
		} else {
			console.error(`Instance of EditContentGroup could not find its ExpandableArea.`);
		}
	}

	private onBodyOrHeaderClick = (event: MouseEvent) => {
		const nodeControls = EditContentNodeControls.getInstance(this.options.getValues().nodeId);
		if (nodeControls !== undefined) {
			nodeControls.focus();
		}
		event.stopPropagation();
	}

	private onChange = () => {
		this.determineIfContainsFocusOrMessagesOrExpandChange(false);
	}
	private determineIfContainsFocusOrMessagesOrExpandChange = (expandIfFocusOrMessage: boolean) => {
		// Determine if the nodes in this group have focus
		// And, determine if the node contains a field message
		let containsFocus = false;
		let childHasFocus = false; // False means an descendant but not the child of this group
		let containsMessage = false;

		const editContentNodes = ComponentManager.queryForAllComponents(this.rootElement, EditContentNode);
		for (let i = 0; i < editContentNodes.length; i++) {
			let node = editContentNodes[i]!;
			if (node.isFocused()) {
				containsFocus = true;
				const focusNodeDepth = node.getDepth();
				if (focusNodeDepth - 1 === this.getDepth()) {
					childHasFocus = true;
				}
			}
			if (node.containsMessage()) {
				containsMessage = true;
			}
			if (containsFocus && containsMessage) {
				break;
			}
		}

		// Set results on this group
		this.setContainsFocus(containsFocus, childHasFocus);
		this.setContainsMessage(containsMessage);

		// During setup of new group, make sure this group is expanded if it contains focus or contains a message
		if (expandIfFocusOrMessage && (containsFocus || containsMessage)) {
			this.setExpanded(true);
		}

		// Update style on group which indicates if the body is expanded (or open) or not.
		const expandableArea = this.getExpandableArea();
		if (expandableArea !== undefined) {
			this.rootElement.classList.toggle(EditContentGroup.expandedBodyStyleClass, expandableArea.isExpanded());
		}
	}

	public getDepth(): number {
		return this.options.getValues().depth;
	}

	private setContainsFocus(focusing: boolean, onChild: boolean) {
		this.rootElement.classList.toggle(EditContentGroup.focusedStyleClass, focusing);
		this.rootElement.classList.toggle(EditContentGroup.focusedChildStyleClass, focusing && onChild);
	}

	private setContainsMessage(hasMessage: boolean) {
		this.rootElement.classList.toggle(EditContentGroup.containsMessageStyleClass, hasMessage);
		const expandableControlElement = this.getExpandableControl();
		if (expandableControlElement !== undefined) {
			expandableControlElement.setTooltip(hasMessage ? "Area contains a validation message" : null);
		}
	}

	private getExpandableControl() {
		return ComponentManager.queryForComponent(
			this.rootElement, ExpandableControl,
			":scope > .editContentGroup__header .editContentGroup__expandableControl"
		);
	}

	private setExpanded(isExpand: boolean) {
		this.getExpandableControl()?.setExpanded(isExpand);
	}

	private getHeader(): HTMLElement | null {
		return this.rootElement.querySelector(":scope > .editContentGroup__header");
	}

	private getBody(): HTMLElement | null {
		return this.rootElement.querySelector(":scope > .editContentGroup__body");
	}
	private getExpandableArea(): ExpandableArea | undefined {
		const body = this.getBody();
		if (body === null) {
			return undefined;
		}
		return ComponentManager.getComponent(body, ExpandableArea);
	}

	public updateSubLabel(editContentNode: EditContentNode, value: string|boolean) {
		const mapKeyOptions = this.options.getValues().mapKeyOptions;
		if (mapKeyOptions === null) {
			return;
		}
		const definitionName = editContentNode.getDefinitionName();
		if (definitionName !== null && definitionName !== undefined) {
			const optionIndex = mapKeyOptions.indexOf(definitionName);
			if (optionIndex > -1) {
				const subLabel = this.rootElement.querySelector(":scope .expandableControl__sublabel");
				if (subLabel !== null) {
					const labelValueIndexString = subLabel.getAttribute('data-map-key-option-index');
					const labelValueIndex = labelValueIndexString !== null ? parseInt(labelValueIndexString) : Number.MAX_SAFE_INTEGER;
					if (optionIndex <= labelValueIndex) {
						subLabel.textContent = (typeof value !== 'string' || value.length > 0 ? `(${value})` : '');
						subLabel.setAttribute('data-map-key-option-index', optionIndex.toString());
					}
				}
			}
		}
	}
}
